/**
 * Canonical PII pattern set — the ONE place in the app bundle where the
 * regexes that recognise a personal identifier live.
 *
 * Two consumers, two different jobs, the same patterns:
 *  - `message-filter.ts` redacts disintermediation attempts in chat with
 *    `[***]` before a message is stored.
 *  - `sanitize.ts` (`stripPII`) redacts telemetry before it leaves the
 *    device, labelling what it removed (`[EMAIL]` / `[PHONE]`).
 *
 * Phase 2 of the audit found the two had diverged: `stripPII` carried a
 * lone naive email regex plus a phone regex whose country code AND
 * separators were both optional, so it matched any 8–11 digit run
 * (prices, timestamps, ids) while missing obfuscated emails and
 * spelled-out numbers. The chat filter had already been hardened against
 * exactly those cases, so the chat patterns are the ones that survived.
 *
 * The Deno telemetry ingest (`supabase/functions/record-product-events`)
 * keeps its own key-based redactor: it runs in another runtime and cannot
 * import from this bundle.
 */

/** Email addresses — plain and obfuscated (`user [at] domain [dot] com`). */
export const PII_EMAIL_PATTERNS: readonly RegExp[] = [
  // Standard email
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  // Obfuscated: user [at] domain [dot] com
  /[a-zA-Z0-9._%+-]+\s*[[(]\s*(?:at|arroba)\s*[\])]\s*[a-zA-Z0-9.-]+\s*[[(]\s*(?:dot|punto)\s*[\])]\s*[a-zA-Z]{2,}/gi,
]

/**
 * Phone numbers. Every pattern demands evidence that the digits are a
 * phone number — a `+` prefix, a separator, a Spanish/English cue word,
 * or digits spelled out — because the previous separator-optional regex
 * redacted prices (`2,503.50`), timestamps and ids.
 */
export const PII_PHONE_PATTERNS: readonly RegExp[] = [
  // Explicit international prefix (+503, +1, etc.) followed by 7-14 digits.
  // High-confidence — an amount would not normally include a leading '+'.
  /\+\d{1,3}[\s.-]?\(?\d{2,4}\)?[\s.-]?\d{3,4}[\s.-]?\d{3,4}/g,
  // Salvadoran mobile (starts with 6 or 7) with an explicit separator.
  /\b[67]\d{3}[\s.-]\d{4}\b/g,
  // 7-666-1234 / 7.666.1234 — the country-style grouping people type by hand.
  /\b[67][\s.-]\d{3}[\s.-]\d{4}\b/g,
  // Digit-by-digit cadence: "7 6 6 6 1 2 3 4". Separators are required so a
  // bare 8-digit price is not redacted.
  /\b[67](?:[\s.-]\d){7}\b/g,
  // Salvadoran mobile written without a separator when immediately
  // preceded by an obvious phone cue in Spanish/English.
  /\b(?:tel[eé]fono|tel|phone|cel|celular|contactame|contacto|wsp|whatsapp|llama|ll[aá]mame)\s*[:#]?\s*[67]\d{7}\b/gi,
  // Spelled out digits (Spanish): "siete nueve ocho nueve ..."
  /\b(cero|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve)(\s+(cero|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve)){5,}\b/gi,
]

/**
 * Keys whose *value* is personal data or a credential regardless of how
 * it is formatted.
 *
 * Free-text regexes cannot help with structured telemetry: `76661234`
 * standing alone in `{ phone: '76661234' }` carries no cue for
 * `PII_PHONE_PATTERNS` to latch onto, but the key says everything. Used
 * by the Sentry scrubber on breadcrumb data, event extras and request
 * data. Deliberately does NOT include a bare `name`, which would redact
 * `modelName` / `osName` out of the device context and blind us on the
 * exact crashes those fields exist to explain.
 */
export const PII_SENSITIVE_KEY =
  /pass(word|code)?|otp|secret|token|authorization|cookie|api[_-]?key|e?mail|correo|phone|tel[eé]fono|celular|whatsapp|dui|nit|licen[cs]e|licencia|address|direcci[oó]n|card|cvv|cvc|iban|account.?number|full.?name|first.?name|last.?name|apellido|birth|nacimiento|(?:profile|user|rental|booking|vehicle|reservation|document|payment|transaction|inspection|guest|host|session|message|thread|event)[_-]?id/i

export interface PatternRedaction {
  /** The text with every match replaced. */
  text: string
  /** True when at least one pattern matched. */
  matched: boolean
}

/**
 * Replace every match of `patterns` in `text` with `replacement`.
 *
 * Each pattern is recompiled per call because the exported regexes carry
 * the global flag, and a shared global regex keeps `lastIndex` between
 * calls — which makes `.test()` skip every other match.
 */
export function redactPatterns(
  text: string,
  patterns: readonly RegExp[],
  replacement: string,
): PatternRedaction {
  let output = text
  let matched = false

  for (const pattern of patterns) {
    if (!new RegExp(pattern.source, pattern.flags).test(output)) continue
    matched = true
    output = output.replace(new RegExp(pattern.source, pattern.flags), replacement)
  }

  return { text: output, matched }
}
